// ================================================================
// 🛒 ABANDONMENTS REPORT - Relatório de Abandonos
// ================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Alert, Button } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import StatCard from '../../components/common/StatCard';
import FilterBar from '../../components/reports/FilterBar';
import FilterDrawer from '../../components/reports/FilterDrawer';
import SmartTable from '../../components/reports/SmartTable';
import ReportsService from '../../services/api/reports';
import MasterLayout from '../../masterLayout/MasterLayout';
import '../../components/reports/Reports.css';
import { useChartsColors } from '../../hooks/useChartsColors';
ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend);

const AbandonmentsReport = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [abandonmentsData, setAbandonmentsData] = useState(null);
  const [filters, setFilters] = useState({
    period: '30d',
    advancedFilters: {}
  });
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const chartsGridColor = useChartsColors();

    const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    scales: {
      x: {
        grid: {
          color: chartsGridColor
        }
      },
      y: {
        grid: {
          color: chartsGridColor
        },
      }
    }
  };

  const loadAbandonmentsReportWithFilters = useCallback(async (filtersToUse) => {
    try {
      setLoading(true);
      setError(null);

      console.log('🛒 Carregando Relatório de Abandonos com filtros...', filtersToUse);

      const result = await ReportsService.getAbandonmentsReport({
        period: filtersToUse.period,
        product_id: filtersToUse.advancedFilters?.product_id
      });

      if (result.success) {
        setAbandonmentsData(result.data);
        console.log('✅ Relatório de Abandonos carregado com filtros:', result.data);
      } else {
        throw new Error(result.message || 'Erro ao carregar relatório');
      }
    } catch (err) {
      console.error('❌ Erro ao carregar relatório:', err);
      setError(err.response?.data?.message || err.message || 'Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAbandonmentsReport = useCallback(async () => {
    loadAbandonmentsReportWithFilters(filters);
  }, [filters, loadAbandonmentsReportWithFilters]);

  useEffect(() => {
    loadAbandonmentsReport();
  }, [loadAbandonmentsReport]);

  const handlePeriodChange = (newPeriod) => {
    setFilters(prev => {
      const newFilters = { ...prev, period: newPeriod };
      setTimeout(() => loadAbandonmentsReportWithFilters(newFilters), 50);
      return newFilters;
    });
  };

  const handleAdvancedFilters = (advancedFilters) => {
    setFilters(prev => {
      const newFilters = { ...prev, advancedFilters };
      setTimeout(() => loadAbandonmentsReportWithFilters(newFilters), 50);
      return newFilters;
    });
  };

  const abandonmentTrendData = abandonmentsData?.charts?.abandonmentTrend ? {
    labels: abandonmentsData.charts.abandonmentTrend.map(item => new Date(item.date).toLocaleDateString('pt-BR')),
    datasets: [{
      label: 'Abandonados',
      data: abandonmentsData.charts.abandonmentTrend.map(item => item.abandoned_count),
      borderColor: '#dc3545',
      backgroundColor: 'rgba(220, 53, 69, 0.1)',
      tension: 0.4
    }, {
      label: 'Completados',
      data: abandonmentsData.charts.abandonmentTrend.map(item => item.completed_count),
      borderColor: '#28a745',
      backgroundColor: 'rgba(40, 167, 69, 0.1)',
      tension: 0.4
    }]
  } : { labels: [], datasets: [] };

  const abandonmentsByProductData = abandonmentsData?.charts?.abandonmentsByProduct ? {
    labels: abandonmentsData.charts.abandonmentsByProduct.map(item => item.product_name?.substring(0, 20) + '...'),
    datasets: [{
      label: 'Taxa de Abandono (%)',
      data: abandonmentsData.charts.abandonmentsByProduct.map(item => item.abandonment_rate),
      backgroundColor: '#dc3545',
      borderWidth: 0
    }]
  } : { labels: [], datasets: [] };

  const abandonmentsColumns = abandonmentsData?.charts?.abandonmentsByProduct ? [
    {
      key: 'product_name',
      label: 'Produto',
      sortable: true
    },
    {
      key: 'abandoned_count',
      label: 'Abandonados',
      sortable: true
    },
    {
      key: 'completed_count',
      label: 'Completados',
      sortable: true
    },
    {
      key: 'abandonment_rate',
      label: 'Taxa (%)',
      sortable: true,
      render: (value) => `${parseFloat(value).toFixed(2)}%`
    },
    {
      key: 'abandoned_value_formatted',
      label: 'Valor Abandonado',
      sortable: true
    }
  ] : [];

  return (
    <MasterLayout>
      <Container fluid className="reports-container" style={{ padding: '24px', maxWidth: '100%' }}>
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Icon icon="mdi:cart-remove" style={{ fontSize: '22px', color: '#dc3545' }} />
                <span className='text-contrast' style={{ fontSize: '22px', fontWeight: '700', display: 'block' }}>
                  Relatório de Abandonos
                </span>
              </div>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <Button
                  variant="outline-primary"
                  onClick={() => setShowFilterDrawer(true)}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', borderRadius: '8px', padding: '10px 20px' }}
                  disabled={loading}
                >
                  <Icon icon="mdi:filter-variant" style={{ fontSize: '20px' }} />
                  Filtros Avançados
                </Button>
                <Button
                  variant="outline-success"
                  onClick={() => console.log('Exportar CSV')}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', borderRadius: '8px', padding: '10px 20px' }}
                  disabled={loading}
                >
                  <Icon icon="mdi:file-excel" style={{ fontSize: '20px' }} />
                  Exportar CSV
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={loadAbandonmentsReport}
                  disabled={loading}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', borderRadius: '8px', padding: '10px 20px' }}
                >
                  <Icon icon="mdi:refresh" style={{ fontSize: '20px' }} />
                  Atualizar
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        {/* Filter Bar */}
        <FilterBar
          period={filters.period}
          onPeriodChange={handlePeriodChange}
          onApplyAdvancedFilters={handleAdvancedFilters}
          onRefresh={loadAbandonmentsReport}
          loading={loading}
          advancedFilters={filters.advancedFilters}
          onUserFilterChange={() => {}}
        />

        <hr style={{ margin: '24px 0', borderTop: '2px solid #dee2e6' }} />

        {/* Error State */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            <Icon icon="mdi:alert-circle" style={{ marginRight: '8px' }} />
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Carregando...</span>
            </div>
            <p style={{ marginTop: '16px', color: '#6c757d' }}>Carregando dados de abandonos...</p>
          </div>
        )}

        {/* Main Content */}
        {!loading && abandonmentsData && (
          <>
            {/* KPIs */}
            <Row className="mb-4">
              <Col xs={12} sm={6} lg={3} className="mb-3">
                <StatCard
                  title={abandonmentsData.kpis.abandonment_rate.label}
                  value={abandonmentsData.kpis.abandonment_rate.formatted}
                  icon="mdi:trending-down"
                  color="#dc3545"
                />
              </Col>
              <Col xs={12} sm={6} lg={3} className="mb-3">
                <StatCard
                  title={abandonmentsData.kpis.abandoned_carts.label}
                  value={abandonmentsData.kpis.abandoned_carts.value}
                  icon="mdi:cart-remove"
                  color="#ffc107"
                />
              </Col>
              <Col xs={12} sm={6} lg={3} className="mb-3">
                <StatCard
                  title={abandonmentsData.kpis.completed_sales.label}
                  value={abandonmentsData.kpis.completed_sales.value}
                  icon="mdi:cart-check"
                  color="#28a745"
                />
              </Col>
              <Col xs={12} sm={6} lg={3} className="mb-3">
                <StatCard
                  title={abandonmentsData.kpis.abandoned_value.label}
                  value={abandonmentsData.kpis.abandoned_value.formatted}
                  icon="mdi:currency-usd"
                  color="#17a2b8"
                />
              </Col>
            </Row>

            {/* Charts */}
            <Row className="mb-4">
              <Col md={8} className="mb-4">
                <Card style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                  <Card.Body>
                    <span className='text-contrast' style={{ fontSize: '18px', fontWeight: '600', display: 'block', marginBottom: '16px' }}>
                      Tendência de Abandonos
                    </span>
                    <Line data={abandonmentTrendData} options={chartOptions} />
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4} className="mb-4">
                <Card style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                  <Card.Body>
                    <span className='text-contrast' style={{ fontSize: '18px', fontWeight: '600', display: 'block', marginBottom: '16px' }}>
                      Top 10 Produtos
                    </span>
                    <Bar data={abandonmentsByProductData} options={{ ...chartOptions, indexAxis: 'y' }} />
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Abandonments Table */}
            <Card style={{ borderRadius: '12px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <Card.Body>
                <span className='text-contrast' style={{ fontSize: '18px', fontWeight: '600', display: 'block', marginBottom: '16px' }}>
                  Abandonos por Produto
                </span>
                <SmartTable
                  columns={abandonmentsColumns}
                  data={abandonmentsData.charts.abandonmentsByProduct || []}
                  searchable={true}
                  pagination={true}
                  itemsPerPage={10}
                />
              </Card.Body>
            </Card>
          </>
        )}

        {/* Filter Drawer */}
        <FilterDrawer
          show={showFilterDrawer}
          onHide={() => setShowFilterDrawer(false)}
          onApply={(filters) => {
            setShowFilterDrawer(false);
            handleAdvancedFilters(filters);
          }}
          currentFilters={filters.advancedFilters}
        />
      </Container>
    </MasterLayout>
  );
};

export default AbandonmentsReport;

